import {ActionIcon, Group, Tooltip, createStyles} from '@mantine/core';
import React, {useCallback, useState} from 'react';

import {isS3URLExpired} from 'external-files/SignedExtFileURLProxy';
import type {ScientificModelNode} from 'model-hub/scientific-models/ScientificModelMetadata';
import getTestProps from 'test/lib/cypress/getTestProps';
import * as Colors from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import type {PredictionJobStatus} from './PredictionJobTypes';
import PropertyResultsPreview from './PropertyResultsPreview';
import ResultsPreviewModal from './ResultsPreviewModal';
import StructureResultsPreview from './StructureResultsPreview';

const WINDOW_OPEN_FEATURES = 'noopener,noreferrer';

function ResultsPreviewActions({
  onExpand,
  onDownload,
  downloadRequesting,
}: {
  onExpand: () => void;
  // Omitted when the job has no downloadable structure file (e.g. property-only models).
  onDownload?: () => void;
  downloadRequesting: boolean;
}): JSX.Element {
  const {classes} = useStyles();

  return (
    <Group
      spacing="sm"
      className={classes.previewActions}
      {...getTestProps({component: 'ResultsPreviewCell', element: 'structurePreviewActions'})}
    >
      <Tooltip label="Open in preview" withinPortal={true}>
        <ActionIcon aria-label="Open in preview" variant="tertiary" onClick={onExpand}>
          <BenchlingIcon icon="expand-right-full" />
        </ActionIcon>
      </Tooltip>
      {onDownload != null && (
        <Tooltip label="Download structure" withinPortal={true}>
          <ActionIcon
            aria-label="Download structure"
            variant="tertiary"
            onClick={onDownload}
            disabled={downloadRequesting}
          >
            <BenchlingIcon icon="download" />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
}

export interface ResultsPreviewCellProps {
  jobId: string;
  standardizedOutputDownloadUrl: string | null;
  componentApiIds: ReadonlyArray<string>;
  model: ScientificModelNode;
  status: PredictionJobStatus;
  configJson?: Record<string, unknown> | null;
  onRefreshDownloadUrls?: () => void;
}

export default function ResultsPreviewCell({
  jobId,
  standardizedOutputDownloadUrl,
  componentApiIds,
  model,
  status,
  configJson,
  onRefreshDownloadUrls,
}: ResultsPreviewCellProps): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useStyles();
  const [modalOpen, setModalOpen] = useState(false);

  const propertyDefinitions = model.propertyDefinitions;
  const hasStructure = standardizedOutputDownloadUrl != null;
  const canPreviewProperties =
    model.canHaveProperties && propertyDefinitions.length > 0 && status === 'COMPLETED';

  const openPreviewModal = useCallback((): void => {
    setModalOpen(true);
  }, []);

  const closePreviewModal = useCallback((): void => {
    setModalOpen(false);
  }, []);

  const handleDownload = useCallback((): void => {
    if (standardizedOutputDownloadUrl == null) {
      return;
    }
    if (isS3URLExpired(standardizedOutputDownloadUrl)) {
      onRefreshDownloadUrls?.();
      return;
    }
    window.open(standardizedOutputDownloadUrl, '_blank', WINDOW_OPEN_FEATURES);
  }, [onRefreshDownloadUrls, standardizedOutputDownloadUrl]);

  if (!hasStructure && !canPreviewProperties) {
    return <span className={classes.noPreview}>—</span>;
  }

  return (
    <>
      <ResultsPreviewActions
        onExpand={openPreviewModal}
        onDownload={hasStructure ? handleDownload : undefined}
        downloadRequesting={false}
      />
      <ResultsPreviewModal opened={modalOpen} onClose={closePreviewModal} wide={canPreviewProperties}>
        {hasStructure ? (
          <StructureResultsPreview
            key={`structure-${jobId}`}
            componentApiIds={componentApiIds}
            configJson={configJson}
            downloadUrl={standardizedOutputDownloadUrl}
            model={model}
            onRefreshDownloadUrls={onRefreshDownloadUrls}
            opened={modalOpen}
          />
        ) : null}
        {canPreviewProperties ? (
          <PropertyResultsPreview
            key={`properties-${jobId}`}
            jobId={jobId}
            opened={modalOpen}
            propertyDefinitions={propertyDefinitions}
          />
        ) : null}
      </ResultsPreviewModal>
    </>
  );
}

const useStyles = createStyles((theme) => ({
  previewActions: {
    flexWrap: 'nowrap',
  },
  noPreview: {
    color: Colors.GRAY_DARKER,
    fontSize: theme.fontSizes.sm,
  },
}));
